import React, { useEffect, useState } from 'react'
import toast from 'react-hot-toast'
import { RedPraticaUSR, RedGruppoImmobiliare } from '../../types/red'
import { praticheUsrApi, redApi } from '../../services/api'

const RedPraticheUSR: React.FC = () => {
  const [pratiche, setPratiche] = useState<RedPraticaUSR[]>([])
  const [gruppi, setGruppi] = useState<RedGruppoImmobiliare[]>([])
  const [loading, setLoading] = useState(true)
  const [ricerca, setRicerca] = useState('')
  const [showNuova, setShowNuova] = useState(false)
  const [editing, setEditing] = useState<RedPraticaUSR | null>(null)
  const [form, setForm] = useState({ numero_pratica: '', importo_approvato: '', gruppo_id: '', note: '' })
  const [saving, setSaving] = useState(false)

  const load = () => {
    praticheUsrApi.list().then(res => {
      setPratiche(res.data)
      setLoading(false)
    })
    redApi.gruppi.list().then(res => setGruppi(res.data)).catch(() => {})
  }

  useEffect(load, [])

  const apriNuova = () => {
    setForm({ numero_pratica: '', importo_approvato: '', gruppo_id: '', note: '' })
    setEditing(null)
    setShowNuova(true)
  }

  const apriModifica = (p: RedPraticaUSR) => {
    setForm({
      numero_pratica: p.numero_pratica,
      importo_approvato: p.importo_approvato != null ? String(p.importo_approvato) : '',
      gruppo_id: p.gruppo_id || '',
      note: p.note || '',
    })
    setEditing(p)
    setShowNuova(true)
  }

  const handleSalva = async () => {
    if (!form.numero_pratica.trim()) {
      toast.error('Numero pratica obbligatorio')
      return
    }
    setSaving(true)
    try {
      const payload = {
        numero_pratica: form.numero_pratica.trim(),
        importo_approvato: form.importo_approvato ? parseFloat(form.importo_approvato) : null,
        note: form.note || null,
        ...(editing ? {} : { gruppo_id: form.gruppo_id || null }),
      }
      if (editing) {
        await praticheUsrApi.update(editing.id, payload)
        toast.success('Pratica USR aggiornata')
      } else {
        await praticheUsrApi.create(payload)
        toast.success('Pratica USR creata')
      }
      setShowNuova(false)
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore salvataggio pratica USR')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (p: RedPraticaUSR) => {
    if (!confirm(`Eliminare la pratica USR "${p.numero_pratica}"?`)) return
    try {
      await praticheUsrApi.delete(p.id)
      toast.success('Pratica USR eliminata')
      load()
    } catch (err: any) {
      toast.error(err?.response?.data?.detail || 'Errore eliminazione')
    }
  }

  const testo = ricerca.trim().toLowerCase()
  const filtrate = pratiche.filter(p => {
    if (!testo) return true
    return [p.numero_pratica, p.gruppo_nome, p.note].filter(Boolean).some(v => v!.toLowerCase().includes(testo))
  })

  if (loading) return <div className="p-8 text-gray-500">Caricamento...</div>

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Pratiche USR</h1>
          <p className="text-sm text-gray-500 mt-1">{filtrate.length} pratiche nel portafoglio</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            className="input text-sm"
            placeholder="Cerca numero, gruppo, note..."
            value={ricerca}
            onChange={e => setRicerca(e.target.value)}
          />
          <button onClick={apriNuova} className="btn-primary">+ Nuova</button>
        </div>
      </div>

      {showNuova && (
        <div className="card p-5 mb-6 max-w-xl">
          <h2 className="text-lg font-semibold mb-4">{editing ? 'Modifica pratica USR' : 'Nuova pratica USR'}</h2>
          <div className="grid grid-cols-2 gap-3 mb-3">
            <div className="col-span-2">
              <label className="label">Numero pratica *</label>
              <input className="input" value={form.numero_pratica} onChange={e => setForm({ ...form, numero_pratica: e.target.value })} placeholder="USR-2024-001" />
            </div>
            <div>
              <label className="label">Importo approvato (€)</label>
              <input type="number" className="input" value={form.importo_approvato} onChange={e => setForm({ ...form, importo_approvato: e.target.value })} />
            </div>
            <div>
              <label className="label">Gruppo/Condominio</label>
              <select
                className="input"
                value={form.gruppo_id}
                onChange={e => setForm({ ...form, gruppo_id: e.target.value })}
                disabled={!!editing}
                title={editing ? 'Il gruppo collegato non è modificabile da qui' : undefined}
              >
                <option value="">Nessuno (pratica autonoma)</option>
                {gruppi.map(g => <option key={g.id} value={g.id}>{g.nome_gruppo}</option>)}
              </select>
            </div>
          </div>
          <div className="mb-3">
            <label className="label">Note</label>
            <textarea className="input" rows={2} value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} />
          </div>
          <div className="flex gap-3">
            <button onClick={() => setShowNuova(false)} disabled={saving} className="btn-secondary">Annulla</button>
            <button onClick={handleSalva} disabled={saving} className="btn-primary">{saving ? 'Salvataggio...' : 'Salva'}</button>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {filtrate.map(p => (
          <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4 flex items-center justify-between hover:shadow-sm transition-shadow">
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-gray-900">{p.numero_pratica}</h3>
                {p.gruppo_nome && <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-blue-50 text-blue-700">{p.gruppo_nome}</span>}
                {!p.gruppo_id && <span className="text-xs text-gray-400">Pratica autonoma</span>}
              </div>
              <div className="text-xs text-gray-400 mt-1">
                {p.importo_approvato != null && `Importo approvato: €${Number(p.importo_approvato).toLocaleString()}`}
                {p.note && ` · ${p.note}`}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => apriModifica(p)} className="text-xs text-blue-600 hover:underline">Modifica</button>
              <button onClick={() => handleDelete(p)} className="text-xs text-red-500 hover:text-red-700">🗑</button>
            </div>
          </div>
        ))}
        {filtrate.length === 0 && <div className="text-center py-20 text-gray-400">Nessuna pratica USR trovata.</div>}
      </div>
    </div>
  )
}

export default RedPraticheUSR
